import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Descriptions, Table, Button, Space, Tag, Typography, message, Popconfirm, Modal, Checkbox } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import intercompanyService from '@/services/intercompany.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_INTERCOMPANY_TRANSACTION } from '@/constants/approval-entity-types';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import siteMaterialService from '@/services/siteMaterial.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

const { Title } = Typography;

const statusColor: Record<string, string> = {
  Draft: 'default',
  Submitted: 'processing',
  Approved: 'success',
  Posted: 'blue',
  Cancelled: 'error',
};

const IntercompanyTransactionDetailPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { formatCurrency } = useCurrency();

  const { data: tx, isLoading } = useQuery({
    queryKey: ['intercompany-transaction', id],
    queryFn: () => intercompanyService.getTransaction(id!),
    enabled: !!id,
  });

  const { data: affiliates = [] } = useQuery({
    queryKey: ['intercompany-affiliates'],
    queryFn: () => intercompanyService.listAffiliates(),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['intercompany-transaction', id] });
    qc.invalidateQueries({ queryKey: ['intercompany-transactions'] });
    qc.invalidateQueries({ queryKey: ['intercompany-balance'] });
    qc.invalidateQueries({ queryKey: ['approval-summary'] });
  };

  const submitMut = useMutation({
    mutationFn: () => intercompanyService.submitTransaction(id!),
    onSuccess: () => {
      message.success(tc('submitted'));
      invalidate();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const approveMut = useMutation({
    mutationFn: () => intercompanyService.approveTransaction(id!),
    onSuccess: () => {
      message.success(tc('approved'));
      invalidate();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const rejectMut = useMutation({
    mutationFn: () =>
      intercompanyService.rejectTransaction(id!, {
        rejection_reason_code: 'OTHER',
        remarks: 'Rejected from IC detail',
      }),
    onSuccess: () => {
      message.success(t('rejectedReturnedToDraft'));
      invalidate();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const postMut = useMutation({
    mutationFn: (body?: {
      allocate_to_buyer_project_id?: string;
      buyer_wip_warehouse_id?: string;
    }) => intercompanyService.postTransaction(id!, body),
    onSuccess: (data: {
      allocate_to_project_wip?: { stock_transfer?: { id?: string }; message?: string };
      allocate_to_project_wip_error?: string;
    }) => {
      setPostOpen(false);
      if (data?.allocate_to_project_wip_error) {
        message.warning(t('postedButAllocateFailed', { error: data.allocate_to_project_wip_error }));
      } else if (data?.allocate_to_project_wip?.message) {
        message.success(
          t('postedWithAllocateMsg', { message: data.allocate_to_project_wip.message })
        );
      } else {
        message.success(t('postedStockVatCommercial'));
      }
      invalidate();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const cancelMut = useMutation({
    mutationFn: () => intercompanyService.cancelTransaction(id!),
    onSuccess: () => {
      message.success(tc('cancelled'));
      invalidate();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: () => intercompanyService.deleteTransaction(id!),
    onSuccess: () => {
      message.success(t('deleted'));
      navigate('/fin/intercompany/transactions');
    },
    onError: (e: Error) => message.error(e.message),
  });

  const [allocateProjectId, setAllocateProjectId] = React.useState<string | undefined>();
  const [allocateOpen, setAllocateOpen] = React.useState(false);
  const [postOpen, setPostOpen] = React.useState(false);
  const [postAllocateProjectId, setPostAllocateProjectId] = React.useState<string | undefined>();
  const [postAlsoAllocate, setPostAlsoAllocate] = React.useState(false);

  const allocateMut = useMutation({
    mutationFn: () =>
      siteMaterialService.allocateIcToProjectWip(id!, {
        buyer_project_id: allocateProjectId!,
      }),
    onSuccess: (data: { stock_transfer?: { id?: string }; message?: string }) => {
      message.success(data?.message || t('draftStockTransferCreated'));
      setAllocateOpen(false);
      if (data?.stock_transfer?.id) {
        navigate(`/scm/warehouse/stock-transfer/${data.stock_transfer.id}`);
      }
    },
    onError: (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message || e.message || t('allocateFailed'));
    },
  });

  if (isLoading || !tx) {
    return <div style={{ padding: 24 }}>{tc('loading')}</div>;
  }

  const buyerName =
    tx.buyer_company_name ||
    (tx.buyer_company_code ? `${tx.buyer_company_code}` : null) ||
    affiliates.find((a) => a.company_id === tx.buyer_company_id)?.company_name ||
    t('buyer');
  const sellerName =
    tx.seller_company_name ||
    affiliates.find((a) => a.company_id === tx.seller_company_id)?.company_name ||
    t('currentCompany');
  const sellerWh =
    tx.seller_warehouse_code && tx.seller_warehouse_name
      ? `${tx.seller_warehouse_code} — ${tx.seller_warehouse_name}`
      : tx.seller_warehouse_name || null;
  const buyerWh =
    tx.buyer_warehouse_code && tx.buyer_warehouse_name
      ? `${tx.buyer_warehouse_code} — ${tx.buyer_warehouse_name}`
      : tx.buyer_warehouse_name || null;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {tx.ic_number}
          </Title>
          <Tag color={statusColor[tx.status] ?? 'default'}>{tx.status}</Tag>
        </div>
        <Space wrap>
          <Button onClick={() => navigate('/fin/intercompany/transactions')}>{t('backToList')}</Button>
          <ReportDocumentActions
            reportKey="intercompany-transaction"
            params={id ? { id } : null}
            documentId={id}
            sendPermissions={['finance.intercompany.create', 'finance.intercompany.view']}
            emailLabel={tc('email')}
          />
          {tx.status === 'Draft' && (
            <>
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(`/fin/intercompany/transactions/${tx.id}/edit`)}
              >
                {tc('edit')}
              </Button>
              <Button type="primary" loading={submitMut.isPending} onClick={() => submitMut.mutate()}>
                {tc('submit')}
              </Button>
            </>
          )}
          {tx.status === 'Submitted' && !tx.approval_instance_id && (
            <>
              <Button type="primary" loading={approveMut.isPending} onClick={() => approveMut.mutate()}>
                {tc('approve')}
              </Button>
              <Button danger loading={rejectMut.isPending} onClick={() => rejectMut.mutate()}>
                {tc('reject')}
              </Button>
            </>
          )}
          {tx.status === 'Submitted' && !!tx.approval_instance_id && (
            <Button danger loading={rejectMut.isPending} onClick={() => rejectMut.mutate()}>
              {tc('reject')}
            </Button>
          )}
          {tx.status === 'Approved' && (
            <Button type="primary" loading={postMut.isPending} onClick={() => setPostOpen(true)}>
              {t('postStockGlInvoices')}
            </Button>
          )}
          {tx.status === 'Posted' && (
            <Button onClick={() => setAllocateOpen(true)}>{t('allocateToProjectWip')}</Button>
          )}
          {(tx.status === 'Draft' || tx.status === 'Submitted') && (
            <Popconfirm title={t('cancelThisDocument')} onConfirm={() => cancelMut.mutate()}>
              <Button icon={<StopOutlined />} loading={cancelMut.isPending}>
                {tc('cancel')}
              </Button>
            </Popconfirm>
          )}
          {(tx.status === 'Draft' || tx.status === 'Cancelled') && (
            <Popconfirm title={t('permanentlyDelete')} onConfirm={() => deleteMut.mutate()}>
              <Button danger icon={<DeleteOutlined />} loading={deleteMut.isPending}>
                {tc('delete')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label={t('date')}>
            {dayjs(tx.transaction_date).format('DD MMM YYYY')}
          </Descriptions.Item>
          <Descriptions.Item label={t('type')}>{tx.transaction_type}</Descriptions.Item>
          <Descriptions.Item label={t('seller')}>{sellerName}</Descriptions.Item>
          <Descriptions.Item label={t('buyer')}>{buyerName}</Descriptions.Item>
          {sellerWh && <Descriptions.Item label={t('sellerWarehouse')}>{sellerWh}</Descriptions.Item>}
          {buyerWh && <Descriptions.Item label={t('buyerWarehouse')}>{buyerWh}</Descriptions.Item>}
          <Descriptions.Item label={t('subtotal')}>
            {formatCurrency(Number(tx.subtotal), tx.currency)}
          </Descriptions.Item>
          <Descriptions.Item label={t('vat')}>
            {formatCurrency(Number(tx.vat_amount), tx.currency)}
          </Descriptions.Item>
          <Descriptions.Item label={t('grandTotal')}>
            {formatCurrency(Number(tx.grand_total), tx.currency)}
          </Descriptions.Item>
          <Descriptions.Item label={t('postedAt')}>
            {tx.posted_at ? dayjs(tx.posted_at).format('DD MMM YYYY HH:mm') : '—'}
          </Descriptions.Item>
          {(tx.seller_journal_voucher_number || tx.seller_journal_voucher_id) && (
            <Descriptions.Item label={t('sellerJv')}>
              {tx.seller_journal_voucher_number || tx.seller_journal_voucher_id}
            </Descriptions.Item>
          )}
          {(tx.buyer_journal_voucher_number || tx.buyer_journal_voucher_id) && (
            <Descriptions.Item label={t('buyerJv')}>
              {tx.buyer_journal_voucher_number || tx.buyer_journal_voucher_id}
            </Descriptions.Item>
          )}
          {(tx.seller_sales_invoice_number || tx.seller_sales_invoice_id) && (
            <Descriptions.Item label={t('sellerSi')}>
              {tx.seller_sales_invoice_number || tx.seller_sales_invoice_id}
            </Descriptions.Item>
          )}
          {(tx.buyer_supplier_invoice_number || tx.buyer_supplier_invoice_id) && (
            <Descriptions.Item label={t('buyerDpi')}>
              {tx.buyer_supplier_invoice_number || tx.buyer_supplier_invoice_id}
            </Descriptions.Item>
          )}
          {tx.notes && (
            <Descriptions.Item label={t('notes')} span={2}>
              {tx.notes}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {(tx.status === 'Submitted' || tx.approval_instance_id) && (
        <div style={{ marginBottom: 16 }}>
          <ApprovalPanel entityType={ENTITY_TYPE_INTERCOMPANY_TRANSACTION} entityId={tx.id} />
        </div>
      )}

      <Card title={t('lines')}>
        <Table
          rowKey="id"
          pagination={false}
          dataSource={tx.lines ?? []}
          columns={[
            { title: '#', dataIndex: 'line_number', width: 50 },
            { title: t('code'), dataIndex: 'item_code' },
            { title: t('description'), dataIndex: 'item_description' },
            { title: t('qty'), dataIndex: 'quantity', align: 'right' },
            { title: t('uom'), dataIndex: 'uom' },
            { title: t('unitPrice'), dataIndex: 'unit_price', align: 'right' },
            {
              title: t('unitCost'),
              dataIndex: 'unit_cost',
              align: 'right',
              render: (v) => (v != null ? Number(v).toFixed(4) : '—'),
            },
            {
              title: t('lineTotal'),
              dataIndex: 'line_total',
              align: 'right',
              render: (v) => formatCurrency(Number(v), tx.currency),
            },
          ]}
        />
      </Card>

      <Modal
        title={t('postIcGoodsTransfer')}
        open={postOpen}
        onCancel={() => setPostOpen(false)}
        onOk={() => {
          if (postAlsoAllocate && !postAllocateProjectId) {
            message.warning(t('selectBuyerProjectToAllocate'));
            return;
          }
          postMut.mutate(
            postAlsoAllocate && postAllocateProjectId
              ? { allocate_to_buyer_project_id: postAllocateProjectId }
              : undefined
          );
        }}
        confirmLoading={postMut.isPending}
        okText={tc('post')}
      >
        <p style={{ marginBottom: 12 }}>{t('postIcGoodsTransferHint')}</p>
        <Checkbox
          checked={postAlsoAllocate}
          onChange={(e) => setPostAlsoAllocate(e.target.checked)}
          style={{ marginBottom: 12 }}
        >
          {t('allocateToProjectWipAfterPost')}
        </Checkbox>
        {postAlsoAllocate ? (
          <ProjectSelector
            value={postAllocateProjectId}
            onChange={(pid) => setPostAllocateProjectId(pid)}
          />
        ) : null}
      </Modal>

      <Modal
        title={t('allocateIcReceiptToProjectWip')}
        open={allocateOpen}
        onCancel={() => setAllocateOpen(false)}
        onOk={() => {
          if (!allocateProjectId) {
            message.warning(t('selectBuyerProject'));
            return;
          }
          allocateMut.mutate();
        }}
        confirmLoading={allocateMut.isPending}
        okText={t('createDraftStockTransfer')}
      >
        <p style={{ marginBottom: 12 }}>{t('allocateIcReceiptHint')}</p>
        <ProjectSelector value={allocateProjectId} onChange={(pid) => setAllocateProjectId(pid)} />
      </Modal>
    </div>
  );
};

export default IntercompanyTransactionDetailPage;
